<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div v-if="isLoading" class="text-center p-5">
                        <div class="spinner-border text-success" role="status">
                            <span class="visually-hidden">Cargando...</span>
                        </div>
                    </div>

                    <div v-else class="table-responsive p-0">
                        <ClientsTable
                            :clients="pagedRows"
                            @client-deleted="fetchClients"
                            :searchTerm="searchTerm"
                            @update:searchTerm="searchTerm = $event"
                        />
                    </div>

                    <div v-if="!isLoading && filteredRows.length > 0" class="card-footer d-flex justify-content-between align-items-center">

                        <div class="d-flex align-items-center">
                            <small class="text-muted me-3">
                                Mostrando {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredRows.length) }} de {{ filteredRows.length }} registros filtrados.
                            </small>

                            <select class="form-select form-select-sm d-inline-block w-auto" v-model="itemsPerPage">
                                <option :value="10">10 por página</option>
                                <option :value="25">25 por página</option>
                                <option :value="50">50 por página</option>
                                <option :value="100">100 por página</option>
                            </select>
                        </div>

                        <nav aria-label="Page navigation example">
                            <ul class="pagination pagination-primary">
                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                    <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                                        <i class="fa fa-angle-left"></i>
                                    </a>
                                </li>
                                <li
                                    class="page-item"
                                    v-for="page in visiblePages"
                                    :key="page"
                                    :class="{ active: currentPage === page }"
                                >
                                    <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                                </li>
                                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                    <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                                        <i class="fa fa-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </nav>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import ClientsTable from "./components/Table.vue";
import Swal from 'sweetalert2';

const companyId = localStorage.getItem('companyId');

export default {
    name: "ClientsView",
    components: {
        ClientsTable,
    },
    data() {
        return {
            clients: [],
            searchTerm: '',
            currentPage: 1,
            itemsPerPage: 10,
            isLoading: true,
        };
    },
    computed: {
        // 1. EL FILTRO DEBE IR PRIMERO: Buscamos en toda la lista descargada (this.clients)
        filteredRows() {
            if (!this.searchTerm) {
                return this.clients;
            }
            const searchLower = this.searchTerm.toLowerCase();
            return this.clients.filter(client => {
                // Buscamos en Nombre, Documento (NIT) y Teléfono
                const clientName = (client.name || '').toString().toLowerCase();
                const businessName = (client.business_name || '').toString().toLowerCase();
                const document = (client.document || client.nit || '').toString().toLowerCase();
                const phone = (client.phone || '').toString().toLowerCase();

                return clientName.includes(searchLower) ||
                       businessName.includes(searchLower) ||
                       document.includes(searchLower) ||
                       phone.includes(searchLower);
            });
        },
        // 2. LA PAGINACIÓN DESPUÉS: Cortamos solo los resultados filtrados
        pagedRows() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredRows.slice(start, end);
        },
        // 3. TOTALES: Se calculan sobre filteredRows para que el paginador se ajuste al buscar
        totalPages() {
            return Math.ceil(this.filteredRows.length / this.itemsPerPage);
        },
        visiblePages() {
            const range = 2;
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        }
    },
    watch: {
        searchTerm() {
            this.currentPage = 1;
        },
        itemsPerPage() {
            this.currentPage = 1;
        }
    },
    mounted() {
        this.fetchClients();
    },
    methods:{
        async fetchClients() {
            if (!companyId) {
                this.isLoading = false;
                console.error("ID de compañía no encontrado.");
                return;
            }

            this.isLoading = true;
            const token = localStorage.getItem("accessToken");
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            try {
                // Cargamos todos los clientes de la compañía
                const clientsResult = await axios.get(`${this.$store.state.apiURL}clients?company=${companyId}`, config);
                this.clients = clientsResult.data;
                this.currentPage = 1;

            } catch (error) {
                this.handleSessionError(error);
            } finally {
                this.isLoading = false;
            }
        },

        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
            }
        },

        handleSessionError(error) {
            const self = this;
            if (error.response && error.response.status === 401) {
                Swal.fire({
                    title: 'Sesión',
                    text: 'El tiempo de sesión ha vencido, ingresa nuevamente.',
                    icon:'warning',
                    confirmButtonColor:'#bf4f51'
                }).then(function(){
                    localStorage.removeItem('user');
                    localStorage.removeItem('accessToken');
                    self.$router.push('/signin');
                });
            } else {
                console.error("Error al obtener datos:", error);
                Swal.fire('Error de Carga', 'No se pudieron obtener los datos.', 'error');
            }
        }
    },
};
</script>

<style scoped>
.pagination { margin: 0; }
.page-link { cursor: pointer; }
.form-select-sm {
    height: calc(1.5em + 0.5rem + 2px);
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    font-size: 0.75rem;
}
</style>